/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
// @mui Material components
// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
// @mui Material components
import { Typography, Icon, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";

import CrudService from "services/cruds-service";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import CoursesModal from "./add-courses";

// Services
import crudsService from "services/cruds-service";

const MODE = {
  EDIT: 'edit',
};

function UserManagement() {
  let { state } = useLocation();
  const [user, setUsers] = useState([]);
  const [notification, setNotification] = useState({
    value: false,
    text: "",
  });
  const [allCourses, setAllCourses] = useState([]);

  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [tableData, setTableData] = useState([]);

  const [couponName, setCouponName] = useState("");
  const [couponEmail, setCouponEmail] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [couponError, setCouponError] = useState("");
  const [mode, setMode] = useState(MODE.EDIT);  // Set default mode to 'add'

  useEffect(() => {
    (async () => {
      const response = await CrudService.getUsers();
      setUsers(response.data);
    })();
  }, []);

  // Fetch courses on component mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const courses = await crudsService.getCourses();
        // Filter courses where external_link is not an empty string
        let editedCourses = courses.data
          .map((course) => ({
            id: course.id,
            app_name: course.attributes.app_name,
          }));

        setAllCourses(editedCourses);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    if (!state) return;
    setNotification({
      value: state.value,
      text: state.text,
    });
  }, [state]);

  useEffect(() => {
    setTableData(getRows(user));
  }, [user]);

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: "",
        });
      }, 5000);
    }
  }, [notification]);

  const handleEditCoupon = async () => {
    await handleUpdateAvailableCourses(selectedUser, selectedCourses);
  };


  const handleUpdateAvailableCourses = async (user, available_courses) => {
    try {
      // Extract only the course IDs as strings (Frontend should not use ObjectId)
      const courseIds = available_courses.map(course => course.id);
      await CrudService.updateAvailableCourses({
        user_id: user,
        available_courses: courseIds,
      });
      alert("Courses updated successfully.");
      window.location.reload();
    } catch (error) {
      alert("Error updating the available course. Please try again later.");
    }
  };


  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const clickAddHandler = () => {
    navigate("/user-management/new-user");
  };

  const clickEditHandler = (id) => {
    navigate(`/user-management/edit-user/${id}`);
  };

  const clickDeleteHandler = async (id) => {
    try {
      if (!confirm("Are you sure you want to delete this user?")) {
        return;
      }

      await CrudService.deleteUser(id);
      const response = await CrudService.getUsers();
      setUsers(response.data);
      setNotification({
        value: true,
        text: "The user has been successfully deleted",
      });
    } catch (err) {
      if (err.hasOwnProperty("errors")) {
        setNotification({
          value: true,
          text: err.errors[0].title,
        });
      }
    }
  };

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        type: "users",
        id: row.id,
        name: row.name,
        email: row.email,
        created_at: new Date(parseInt(row.id.substring(0, 8), 16) * 1000).toDateString(),
        courses_taken: row.available_courses?.length,
        organization: row.organization,
        organization_role: row.organization_role,
        available_courses: row.available_courses,
      };
    });
    updatedInfo = updatedInfo.sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    );
    return updatedInfo;
  };

  const handleSetSelectedCourses = (availableCourseIds, _id) => {
    // Filter the already fetched courses that match the available course IDs
    const selectedCourses = allCourses.filter((course) =>
      availableCourseIds.includes(course.id) // Match course ID with the available_courses list
    );
    // Set the selected courses in the state
    setSelectedCourses(selectedCourses);
    setSelectedUser(_id);
  };



  const dataTableData = {
    columns: [
      { Header: "name", accessor: "name", width: "10%" },
      { Header: "email", accessor: "email", width: "10%" },
      { Header: "created at", accessor: "created_at", width: "10%" },
      { Header: "courses", accessor: "courses_taken", width: "5%" },
      { Header: "organization", accessor: "organization", width: "15%" },
      { Header: "role", accessor: "organization_role", width: "10%" },
      {
        Header: "Actions",
        width: "5%",
        Cell: (info) => (
          <MDBox display="flex" alignItems="center" style={{ justifyContent: "space-around", gap: "10px" }}>
            {/* Edit User */}
            <Icon
              color="info"
              fontSize="medium"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setCouponName(info.cell.row.original.name);
                setCouponEmail(info.cell.row.original.email);
                handleSetSelectedCourses(info.cell.row.original.available_courses, info.cell.row.original.id);
                setMode(MODE.EDIT);
                handleOpenModal();
              }}
            >
              edit
            </Icon>
            {/* Delete User */}
            <Icon
              color="error"
              fontSize="medium"
              style={{ cursor: "pointer" }}
              onClick={() => clickDeleteHandler(info.cell.row.original.id)}
            >
              delete
            </Icon>

          </MDBox>
        ),
      },
    ],

    rows: tableData,
  };

  // Close Modal and reset inputs
  const handleCloseAddModal = () => {
    setCouponName("");
    setCouponEmail("");
    setCouponError("");
    setSelectedCourses([]);
    setOpenModal(false);
  };

  return (
    <>
      <CoursesModal
        open={openModal}
        handleClose={handleCloseAddModal}
        couponName={couponName}
        setCouponName={setCouponName}
        couponEmail={couponEmail}
        setCouponEmail={setCouponEmail}
        selectedCourses={selectedCourses}
        setSelectedCourses={setSelectedCourses}
        allCourses={allCourses}
        handleEditCoupon={handleEditCoupon}
        couponError={couponError}
        setCouponError={setCouponError}
        currentMode={mode}
        MODE={MODE}
      />
      <DashboardLayout>
        <DashboardNavbar />
        <MDTypography variant="h3" mt={2}>
          User Management
        </MDTypography>
        <Divider />
        {notification.value && (
          <MDAlert color="info" my="20px">
            <MDTypography variant="body2" color="white">
              {notification.text}
            </MDTypography>
          </MDAlert>
        )}
        <MDBox pt={1} pb={3}>
          <MDBox mb={3}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography variant="h5" fontWeight="medium">
                  Users
                </MDTypography>
                {/* {ability.can("create", "users") && (
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    type="submit"
                    onClick={clickAddHandler}
                  >
                    + Add User
                  </MDButton>
                )} */}
              </MDBox>
              <DataTable table={dataTableData} canSearch />
            </Card>
          </MDBox>
        </MDBox>
      </DashboardLayout>
    </>

  );
}

export default UserManagement;
